/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';

import styles from './SplitCardElement.module.css';

const FIELDS = {
  NUMBER: 'number',
  EXPIRY: 'expiry',
  CVC: 'cvc',
};

// Each Stripe field is a separate iframe. Auto-advancing focus across iframes
// behaves well only on iOS; on Android it makes the soft keyboard close and
// re-open (a visible "jump"), and on desktop it isn't wanted. So we enable
// auto-advance on iOS only.
const IS_IOS =
  typeof navigator !== 'undefined' &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent || '') ||
    // iPadOS 13+ reports as "Macintosh" but is a touch device
    (/Macintosh/.test(navigator.userAgent || '') && (navigator.maxTouchPoints || 0) > 1));

// Text styling for the Stripe-hosted iframes (the visible field box is styled
// in SplitCardElement.module.css — Stripe only controls the text/placeholder).
const BASE_ELEMENT_STYLE = {
  base: {
    fontSize: '16px',
    color: '#1a1a1a',
    fontFamily: 'inherit',
    '::placeholder': {
      color: '#9a9a9a',
    },
  },
  invalid: {
    color: '#e5424d',
  },
};

/**
 * Stripe card form split into separate fields: card number on its own row,
 * expiry date and CVC side by side underneath. The postal/zip code is disabled
 * here — Stripe's combined CardElement is the only one that shows it inline.
 * Focus auto-advances to the next field once the current one is complete.
 */
export const SplitCardElement = React.forwardRef(
  ({ className, onChange, onReady }, ref) => {
    const elementOptions = useMemo(
      () => ({
        style: BASE_ELEMENT_STYLE,
      }),
      []
    );

    // Stripe element instances, captured from each field's onReady — used to
    // move focus to the next field programmatically.
    const expiryElRef = useRef(null);
    const cvcElRef = useRef(null);

    const [ready, setReady] = useState({
      [FIELDS.NUMBER]: false,
      [FIELDS.EXPIRY]: false,
      [FIELDS.CVC]: false,
    });

    const [valid, setValid] = useState({
      [FIELDS.NUMBER]: false,
      [FIELDS.EXPIRY]: false,
      [FIELDS.CVC]: false,
    });

    const [touched, setTouched] = useState(false);

    const handleReady = (field, element) => {
      if (field === FIELDS.EXPIRY) expiryElRef.current = element;
      if (field === FIELDS.CVC) cvcElRef.current = element;

      setReady((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    };

    const handleChange = (field, e) => {
      if (e.empty === false && !touched) {
        setTouched(true);
      }

      // Auto-advance: jump to the next field as soon as this one is complete.
      // Enabled on iOS only — on Android it flickers the keyboard, on desktop
      // it isn't wanted.
      if (e.complete && IS_IOS) {
        if (field === FIELDS.NUMBER) expiryElRef.current?.focus();
        else if (field === FIELDS.EXPIRY) cvcElRef.current?.focus();
      }

      setValid((prev) => ({
        ...prev,
        [field]: e.complete,
      }));
    };

    // Notify the form once every field has reported it's ready.
    useEffect(() => {
      const allReady = Object.values(ready).every(Boolean);
      if (allReady && onReady) {
        onReady();
      }
    }, [onReady, ready]);

    // Propagate combined validity to the form (mirrors the recurly element).
    useEffect(() => {
      if (touched && onChange) {
        const allValid = Object.values(valid).every(Boolean);
        onChange({ valid: allValid });
      }
    }, [valid, touched, onChange]);

    return (
      <div ref={ref} className={className}>
        <div className={styles.field}>
          <div className={styles.input}>
            <CardNumberElement
              options={elementOptions}
              onReady={(el) => handleReady(FIELDS.NUMBER, el)}
              onChange={(e) => handleChange(FIELDS.NUMBER, e)}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <div className={styles.input}>
              <CardExpiryElement
                options={elementOptions}
                onReady={(el) => handleReady(FIELDS.EXPIRY, el)}
                onChange={(e) => handleChange(FIELDS.EXPIRY, e)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.input}>
              <CardCvcElement
                options={elementOptions}
                onReady={(el) => handleReady(FIELDS.CVC, el)}
                onChange={(e) => handleChange(FIELDS.CVC, e)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

SplitCardElement.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  onReady: PropTypes.func,
};

SplitCardElement.defaultProps = {
  className: null,
  onChange: null,
  onReady: null,
};

export default SplitCardElement;
